<template>
  <v-app>
    <LoginPage v-if="!authSession" @login-success="handleLoginSuccess" />
    <template v-else>
    <v-main>
      <div class="page-content">
        <div class="page-header">
        <div class="top-btn">
        <v-row class="pa-3" align="center" justify="start">
          <!-- 导航栏 -->
          <v-col cols="3" md="3" style="max-width:250px;">
            <v-menu v-model="navigationExpanded" :close-on-content-click="false">
              <template v-slot:activator="{ props }">
                <v-text-field
                  v-bind="props"
                  :model-value="trajectoryConfigs.length + servos.length > 0 ? `导航 (${trajectoryConfigs.length + servos.length} 项)` : '导航'"
                  label="导航"
                  variant="outlined"
                  density="compact"
                  hide-details
                  readonly
                  prepend-inner-icon="mdi-menu"
                ></v-text-field>
              </template>
              <v-card min-width="300" max-height="400" style="overflow-y: auto;">
                <v-card-text class="pa-2">
                  <div v-if="trajectoryConfigs.length === 0 && servos.length === 0" class="text-center text-grey py-4">
                    暂无舵机或轨迹
                  </div>
                  <div v-else class="d-flex align-center flex-wrap">
                    <template v-for="trajectory in trajectoryConfigs" :key="trajectory.id">
                      <v-btn
                        size="small"
                        color="primary"
                        variant="outlined"
                        class="ma-1"
                        @click="scrollToElement(`trajectory-${trajectory.id}`); navigationExpanded = false"
                      >
                        {{ trajectory.name }}
                      </v-btn>
                    </template>
                    <template v-for="(servo, idx) in servos" :key="`servo-${idx}`">
                      <v-btn
                        size="small"
                        color="secondary"
                        variant="outlined"
                        class="ma-1"
                        @click="scrollToElement(`servo-${servo.s}-${idx}`); navigationExpanded = false"
                      >
                        {{ servo.name }}
                      </v-btn>
                    </template>
                  </div>
                  <v-divider class="my-2" v-if="trajectoryConfigs.length > 0 || servos.length > 0"></v-divider>
                  <div class="d-flex justify-center mt-2" v-if="trajectoryConfigs.length > 0 || servos.length > 0">
                    <v-btn
                      size="small"
                      color="error"
                      variant="outlined"
                      prepend-icon="mdi-delete-sweep"
                      @click="clearAllServosAndTrajectories"
                    >
                      清空
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </v-menu>
          </v-col>
          <v-col cols="3" md="3" style="max-width:250px;">
            <v-select v-model="selectedEnv" :items="envList" item-title="name" return-object label="选择环境"
                      variant="outlined" density="compact" hide-details clearable/>
          </v-col>
          <v-col cols="3" md="3" style="max-width:250px;">
            <v-select v-model="deviceId" :items="deviceList" item-title="name" item-value="id" label="选择设备ID"
                      variant="outlined" density="compact" hide-details clearable :loading="deviceListLoading"
                      @click="fetchDeviceList"/>
          </v-col>
          <v-col cols="3" md="3" style="max-width:200px;">
            <v-text-field :model-value="servoVersion || '—'" label="设备型号"
                          variant="outlined" density="compact" hide-details disabled/>
          </v-col>

          <!-- 新增操作名称输入框 -->
          <v-col cols="3" md="3" style="max-width:200px;">
            <v-text-field v-model="operationName" label="操作名称" variant="outlined" density="compact" hide-details
                          required/>
          </v-col>
          <!-- 新增表情下拉框 -->
          <v-col cols="2" md="2" style="max-width:230px;">
            <v-select
                v-model="selectedEmotion"
                :items="emotionOptions"
                item-title="label"
                item-value="value"
                label="表情"
                variant="outlined"
                density="compact"
                hide-details
                clearable
            />
          </v-col>
          <!-- 版本号 -->
          <v-col cols="2" style="max-width:150px;">
            <v-text-field v-model.number="emojiVersion" label="版本号" type="number" min="1" density="compact"
                          variant="outlined" hide-details/>
          </v-col>
          <v-btn @click="openAddServoDialog" color="primary" prepend-icon="mdi-plus" variant="outlined">
            添加舵机
          </v-btn>
          <v-btn @click="openVelocityLimitDialog" class="ml-2" color="info" prepend-icon="mdi-speedometer" variant="outlined">
            舵机最大速度
          </v-btn>
          <v-btn @click="openAddTrajectoryDialog" class="ml-2" color="primary" prepend-icon="mdi-chart-line" variant="outlined">
            添加轨迹
          </v-btn>
          <v-btn
            @click="toggleTrajectoryChart"
            class="ml-2"
            :color="showTrajectoryChart ? 'grey' : 'primary'"
            :prepend-icon="showTrajectoryChart ? 'mdi-eye-off' : 'mdi-eye'"
            variant="outlined"
          >
            {{ showTrajectoryChart ? '隐藏轨迹图' : '显示轨迹图' }}
          </v-btn>
          <v-btn @click="runServos(false)" class="ml-2" color="success" prepend-icon="mdi-play" variant="outlined">
            开始运行
          </v-btn>
          <v-btn @click="runServos(true)" class="ml-2" color="warning" prepend-icon="mdi-plus" variant="outlined"
                 :disabled="!selectedEmotion">
            保存到设备
          </v-btn>
          <v-btn class="ml-2" color="primary" @click="saveConfirmDialog = true" variant="outlined">保存记录到 Manager
          </v-btn>
          <v-btn class="ml-2" color="warning" @click="sevroDebug = !sevroDebug" variant="outlined">单舵机调试</v-btn>
          <v-btn class="ml-2" color="warning" @click="openFunctionalTestDialog" variant="outlined" prepend-icon="mdi-test-tube">功能性测试</v-btn>
          <!-- 表情开关选择 -->
          <v-col cols="2" md="2" style="max-width:180px;">
            <v-select
              v-model="selectedEmotionState"
              :items="emotionStateOptions"
              item-title="label"
              item-value="value"
              label="表情开关"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              :disabled="!deviceId"
              @update:model-value="handleEmotionStateChange"
            />
          </v-col>
          <!-- 背景动作开关选择 -->
          <v-col cols="2" md="2" style="max-width:180px;">
            <v-select
              v-model="selectedBackgroundActionState"
              :items="backgroundActionStateOptions"
              item-title="label"
              item-value="value"
              label="背景动作开关"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              :disabled="!deviceId"
              @update:model-value="handleBackgroundActionStateChange"
            />
          </v-col>
        </v-row>
        </div>

        <v-menu location="bottom end" offset="8">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              class="user-avatar-button"
              icon
              variant="text"
              aria-label="查看用户信息"
            >
              <v-avatar color="primary" size="40">
                <v-img v-if="userAvatarImageUrl" :src="userAvatarImageUrl" alt="用户头像" cover @error="handleAvatarError" />
                <v-icon v-else size="24">mdi-account</v-icon>
              </v-avatar>
            </v-btn>
          </template>

          <v-card class="user-info-menu-card" variant="outlined">
            <v-card-text class="pa-3">
              <div class="user-info-heading">
                <v-avatar color="primary" size="36">
                  <v-img v-if="userAvatarImageUrl" :src="userAvatarImageUrl" alt="用户头像" cover @error="handleAvatarError" />
                  <v-icon v-else size="22">mdi-account</v-icon>
                </v-avatar>
                <div class="user-info-identity">
                  <div class="user-info-name">{{ userDisplayName }}</div>
                  <div class="user-info-caption">登录环境：{{ loginEnvironmentName }}</div>
                </div>
              </div>

              <v-progress-linear v-if="userInfoLoading" class="my-3" color="primary" indeterminate />
              <v-alert v-else-if="userInfoError" class="my-3" density="compact" type="warning" variant="tonal">
                {{ userInfoError }}
              </v-alert>
              <dl v-else class="user-info-list">
                <div v-for="item in userInfoItems" :key="item.label" class="user-info-item">
                  <dt>{{ item.label }}</dt>
                  <dd :title="item.value">{{ item.value }}</dd>
                </div>
              </dl>

              <v-btn
                block
                class="mt-3"
                color="secondary"
                prepend-icon="mdi-logout"
                variant="outlined"
                @click="logoutConfirmDialog = true"
              >
                退出登录
              </v-btn>
            </v-card-text>
          </v-card>
        </v-menu>
      </div>

      <div class="split-container" :class="{ 'bottom-panel-hidden': !showBottomPanel }">
        <div class="top-panel">
          <div class="pa-3" style="height: 100%; overflow: auto;">
            <v-row>
              <!-- 左侧内容区域 -->
              <v-col cols="7" md="7">
                <!-- 双舵机机械臂视图 -->
                <DualServoArm 
                  v-if="showDualServoArm || dualServos.length > 0"
                  :device-id="deviceId" 
                  :servo-version="servoVersion"
                  :trajectory-config="selectedTrajectoryConfig"
                  :trajectory-x="trajectoryX"
                  :trajectory-y="trajectoryY"
                  :trajectory-points="currentTrajectory?.trajectoryPoints || []"
                  :all-servos-data="allServos"
                  :show-arm-visualization="showDualArmVisualization"
                  @trajectory-saved="handleTrajectorySaved"
                  @exit="exitDualServoArm"
                  @servos-changed="handleDualServosChanged"
                  @arm-config-changed="handleDualArmConfigChanged"
                  @end-position-changed="handleEndPositionChanged"
                  @toggle-visualization="handleArmVisualizationToggle"
                />
                
                <!-- 合并的舵机视图 -->
                <div>  
                  <!-- 多个轨迹配置卡片 -->
                  <TrajectoryCard
                    v-for="trajectory in trajectoryConfigs"
                    :key="trajectory.id"
                    :trajectory="trajectory"
                  />
                  
                  <!-- 单舵机卡片 -->
                  <ServoCard
                    v-for="(servo, sIdx) in servos"
                    :key="`servo-${sIdx}`"
                    :servo="servo"
                    :s-idx="sIdx"
                  />
                </div>
              </v-col>
              <!-- 右侧区域：操作记录表格 -->
              <v-col cols="5" md="5" style="position: sticky; top: 0; align-self: flex-start; max-height: calc(100vh - 200px); overflow-y: auto;">
                <!-- <v-card-text class="pa-0" style="min-height: 400px;"> -->
                <!-- 还原操作记录 -->
                <OperationRecord
                    ref="operationRecordRef"
                    :refresh-trigger="refreshTrigger"
                    :is-super-admin="isSuperAdmin"
                    :current-tenant-id="currentTenantId"
                    :base-manager-url="baseManagerUrl"
                    :device-id="deviceId"
                    @restore-config="handleRestoreConfig"
                    @run-combo="handleComboRequest"
                    @tenant-changed="handleDeviceTenantChange"
                />
              </v-col>
            </v-row>

            <!-- 添加舵机弹窗 -->
            <v-dialog v-model="addDialog" max-width="400">
              <v-card>
                <v-card-title class="text-h6 text-center pb-2" style="font-weight: 500;">选择舵机</v-card-title>
                <v-card-text class="pt-0 pb-2 d-flex flex-column align-center">
                  <div style="width: 100%; max-width: 320px;" class="mb-2">
                    <v-btn size="small" variant="outlined" @click="toggleSelectAll" class="mb-2">
                      {{ isAllSelected ? '取消全选' : '全选' }}
                    </v-btn>
                  </div>
                  <v-list style="width: 100%; max-width: 320px;">
                    <v-list-item v-for="servo in currentServoList" :key="servo.id" style="cursor:pointer;" @click="toggleServoSelection(servo.id)">
                      <template v-slot:prepend>
                        <v-checkbox v-model="selectedServoIds" :value="servo.id" hide-details density="compact" @click.stop></v-checkbox>
                      </template>
                      <v-list-item-content>
                        <v-list-item-title>{{ servo.name }} <span style="font-size:12px;color:#1976d2;font-weight:bold;">[ID: {{ servo.id }}]</span></v-list-item-title>
                        <v-list-item-subtitle class="text-caption">运动范围：{{ servo.range ? servo.range[0] : 0 }} 到
                          {{ servo.range ? servo.range[1] : 0 }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
                <v-card-actions class="pt-0 pb-2 pr-4">
                  <v-spacer/>
                  <v-btn text @click="addDialog = false; selectedServoIds = []" variant="outlined">取消</v-btn>
                  <v-btn color="primary" @click="addSelectedServos" variant="outlined" :disabled="selectedServoIds.length === 0">添加</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <!-- 舵机最大速度设置弹窗 -->
            <v-dialog v-model="velocityLimitDialog" max-width="600">
              <v-card>
                <v-card-title class="text-h6 text-center pb-2" style="font-weight: 500;">设置舵机最大速度</v-card-title>
                <v-card-text class="pt-0 pb-2">
                  <div v-if="!currentServoList || currentServoList.length === 0" class="text-center py-4 text-grey">
                    请先选择设备
                  </div>
                  <v-list v-else style="width: 100%;">
                    <v-list-item 
                      v-for="servo in currentServoList" 
                      :key="servo.id"
                      class="px-0"
                    >
                      <v-list-item-content>
                        <v-list-item-title>{{ servo.name }}</v-list-item-title>
                      </v-list-item-content>
                      <template v-slot:append>
                        <v-text-field 
                          v-model.number="servoVelocityMap[servo.id]" 
                          label="最大速度" 
                          type="number" 
                          min="0" 
                          density="compact"
                          variant="outlined" 
                          hide-details
                          style="width: 150px;"
                        />
                      </template>
                    </v-list-item>
                  </v-list>
                </v-card-text>
                <v-card-actions class="pt-0 pb-2 pr-4">
                  <v-spacer/>
                  <v-btn @click="velocityLimitDialog = false" variant="outlined" class="mr-2">取消</v-btn>
                  <v-btn @click="saveVelocityLimit" color="primary" variant="outlined" :disabled="!currentServoList || currentServoList.length === 0">保存</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <!-- 选择轨迹弹窗 -->
            <v-dialog v-model="selectTrajectoryDialog" max-width="600">
              <v-card>
                <v-card-title class="text-h6 text-center pb-2" style="font-weight: 500;">选择轨迹</v-card-title>
                <v-card-text class="pt-0 pb-2 d-flex flex-column align-center">
                  <v-list style="width: 100%; max-width: 520px;">
                    <v-list-item 
                      v-for="(trajectoryGroup, name) in trajectoryListData" 
                      :key="name" 
                      @click="selectTrajectoryName(name, trajectoryGroup)"
                      style="cursor:pointer;"
                    >
                      <v-list-item-content>
                        <v-list-item-title>{{ name }}</v-list-item-title>
                        <v-list-item-subtitle class="text-caption">
                          {{ isNestedTrajectory(trajectoryGroup) ? `包含 ${Object.keys(trajectoryGroup).length} 个人物配置` : '点击选择人物' }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
                <v-card-actions class="pt-0 pb-2 pr-4">
                  <v-spacer/>
                  <v-btn text @click="selectTrajectoryDialog = false" variant="outlined">取消</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <!-- 选择人物弹窗 -->
            <v-dialog v-model="selectPersonDialog" max-width="600">
              <v-card>
                <v-card-title class="text-h6 text-center pb-2" style="font-weight: 500;">选择人物 - {{ selectedTrajectoryName }}</v-card-title>
                <v-card-text class="pt-0 pb-2 d-flex flex-column align-center">
                  <v-list style="width: 100%; max-width: 520px;">
                    <v-list-item 
                      v-for="(trajectory, personName) in getTrajectoryPersons(selectedTrajectoryName)" 
                      :key="personName" 
                      @click="selectTrajectory(selectedTrajectoryName, personName, trajectory)"
                      style="cursor:pointer;"
                    >
                      <v-list-item-content>
                        <v-list-item-title>{{ personName }}</v-list-item-title>
                        <v-list-item-subtitle class="text-caption">
                          适用版本: {{ trajectory.servoVersions?.join(', ') || 'N/A' }} | 
                          舵机ID: {{ trajectory.ids?.join(', ') || 'N/A' }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
                <v-card-actions class="pt-0 pb-2 pr-4">
                  <v-spacer/>
                  <v-btn text @click="selectPersonDialog = false; selectedTrajectoryName = ''" variant="outlined">返回</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <!-- 镜像轨迹弹窗 -->
            <v-dialog v-model="mirrorTrajectoryDialog" max-width="600">
              <v-card>
                <v-card-title class="text-h6 text-center pb-2" style="font-weight: 500;">选择镜像目标</v-card-title>
                <v-card-text class="pt-0 pb-2 d-flex flex-column align-center">
                  <!-- 显示当前源轨迹信息 -->
                  <div v-if="mirrorTrajectoryId" class="text-caption text-grey-darken-1 mb-3" style="width: 100%; max-width: 520px;">
                    <template v-if="getSourceTrajectoryForMirror">
                      当前轨迹: {{ getSourceTrajectoryForMirror.name }}
                      <span v-if="getSourceTrajectoryForMirror.personName" class="text-primary">
                        (将镜像到相同人物: {{ getSourceTrajectoryForMirror.personName }})
                      </span>
                    </template>
                  </div>
                  <v-list style="width: 100%; max-width: 520px;">
                    <v-list-item 
                      v-for="(trajectoryGroup, name) in trajectoryListData" 
                      :key="name" 
                      @click="mirrorTargetTrajectoryName = name"
                      :class="{ 'bg-blue-lighten-5': mirrorTargetTrajectoryName === name }"
                      style="cursor:pointer;"
                    >
                      <v-list-item-content>
                        <v-list-item-title>{{ name }}</v-list-item-title>
                        <v-list-item-subtitle class="text-caption">
                          {{ isNestedTrajectory(trajectoryGroup) 
                            ? `包含 ${Object.keys(trajectoryGroup).length} 个人物配置` + (getMirrorPersonHint(name, trajectoryGroup) ? ` - ${getMirrorPersonHint(name, trajectoryGroup)}` : '')
                            : `适用版本: ${trajectoryGroup.servoVersions?.join(', ') || 'N/A'} | 舵机ID: ${trajectoryGroup.ids?.join(', ') || 'N/A'}` }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action v-if="mirrorTargetTrajectoryName === name">
                        <v-icon color="primary">mdi-check</v-icon>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </v-card-text>
                <v-card-actions class="pt-0 pb-2 pr-4">
                  <v-spacer/>
                  <v-btn text @click="mirrorTrajectoryDialog = false" variant="outlined">取消</v-btn>
                  <v-btn color="primary" @click="performMirrorTrajectory" variant="outlined" :disabled="!mirrorTargetTrajectoryName">确认镜像</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <!-- 添加返程确认弹窗 -->
            <v-dialog v-model="addReturnTripDialog" max-width="500">
              <v-card>
                <v-card-title class="text-h6">确认添加返程</v-card-title>
                <v-card-text>
                  将使机器人按照已有轨迹点逆序返回起点，确定要添加返程吗？
                </v-card-text>
                <v-card-actions>
                  <v-btn text @click="addReturnTripDialog = false" variant="outlined">取消</v-btn>
                  <v-btn color="primary" @click="confirmAddReturnTrip" variant="outlined">确认添加</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <!-- 删除轨迹确认弹窗 -->
            <v-dialog v-model="deleteTrajectoryDialog" max-width="500">
              <v-card>
                <v-card-title class="text-h6">确认删除</v-card-title>
                <v-card-text>
                  确定要删除轨迹配置"{{ deleteTrajectoryName }}"吗？此操作不可恢复。
                </v-card-text>
                <v-card-actions>
                  <v-btn text @click="deleteTrajectoryDialog = false" variant="outlined">取消</v-btn>
                  <v-btn color="error" @click="confirmDeleteTrajectory" variant="outlined">确认删除</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <!-- 保存确认弹窗 -->
            <v-dialog v-model="saveConfirmDialog" max-width="500">
              <v-card>
                <v-card-title class="text-h6">确认保存</v-card-title>
                <v-card-text>
                  确定要保存当前操作记录吗？
                </v-card-text>
                <v-card-actions>
                  <v-btn text @click="saveConfirmDialog = false" variant="outlined">取消</v-btn>
                  <v-btn color="primary" @click="confirmSave" variant="outlined">确认保存</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <!-- 组合动作执行进度弹窗 -->
            <v-dialog v-model="comboRunning" persistent max-width="440">
              <v-card>
                <v-card-title class="text-h6 d-flex align-center">
                  <v-icon left>mdi-playlist-play</v-icon>组合表情执行中
                </v-card-title>
                <v-divider />
                <v-card-text class="pt-4">
                  <div class="mb-2 text-subtitle-2">{{ comboCurrentName || '准备中...' }}</div>
                  <v-progress-linear
                    :model-value="comboTotal ? (comboCurrentIndex / comboTotal * 100) : 0"
                    height="8"
                    rounded
                    color="primary"
                    class="mb-2"
                  />
                  <div class="text-caption text-grey">已完成 {{ comboCurrentIndex }} / {{ comboTotal }}</div>
                  <div v-if="comboStatusMsg" class="text-caption text-grey mt-1">{{ comboStatusMsg }}</div>
                </v-card-text>
                <v-divider />
                <v-card-actions class="pa-3">
                  <v-spacer />
                  <v-btn color="red" variant="outlined" @click="stopCombo">停止</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>


            <v-snackbar v-model="angleSnackbar" color="primary" timeout="1800" location="bottom right">
              {{ angleSnackbarMsg }}
            </v-snackbar>
          </div>
        </div>
        <div v-show="showBottomPanel" class="splitter" @mousedown="startDrag"></div>
        <div v-show="showBottomPanel" class="bottom-panel">
          <div
            v-if="dualServos.length > 0 && !showDualArmVisualization"
            class="chart-controls"
            style="display: flex; align-items: center; gap: 8px; padding: 8px;"
          >
            <!-- 双舵机模式下的切换按钮 -->
            <v-btn 
              size="small" 
              color="primary" 
              variant="outlined"
              @click="showDualArmVisualization = true"
            >
              切换到机械臂视图
            </v-btn>
          </div>
          <div
            v-show="showTrajectoryChart && !(dualServos.length > 0 && showDualArmVisualization)"
            ref="chartRef"
            class="chart-area"
          ></div>
        </div>
      </div>
      </div>
    </v-main>

    <!-- ECharts 曲线图 -->
    <!-- <div class="position-fixed bottom-0" ref="chartRef" style="width:100%;height:300px;margin-top:32px;"></div> -->

    <v-dialog v-model="logoutConfirmDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">确认退出登录</v-card-title>
        <v-card-text>确定要退出当前账号吗？</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="logoutConfirmDialog = false">取消</v-btn>
          <v-btn color="error" variant="outlined" @click="handleLogout">退出登录</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteServoDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">确认删除</v-card-title>
        <v-card-text>确定要删除该舵机及其所有片段吗？</v-card-text>
        <v-card-actions>
          <v-btn text @click="cancelDeleteServo" variant="outlined">取消</v-btn>
          <v-btn color="red" @click="confirmDeleteServo" variant="outlined">确认删除</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 功能性测试 -->
    <FunctionalTestDialog
      v-model="functionalTestDialog"
      v-model:batch-device-ids="batchTestDeviceIds"
      v-model:loop-count="batchTestLoopCount"
      :devices="deviceList"
      :devices-loading="deviceListLoading"
      :batch-running="comboRunning"
      :tenant-selection-required="isSuperAdmin && !deviceTenantId"
      @open-servo-check="openServoAvailabilityCheck"
      @open-batch-combo="openBatchCombo"
    />

    <!-- 舵机可用性校验弹窗 -->
    <ServoDebugDialog
      v-model="servoDebugDialog"
      :form="servoDebugForm"
      :running="servoDebugRunning"
      @run="runAbsoluteAngleTest"
      @stop="stopAbsoluteAngleTest"
    />

    <!-- 表情/背景动作确认对话框 -->
    <v-dialog v-model="actionConfirmDialog" max-width="400" @update:model-value="handleConfirmDialogClose">
      <v-card>
        <v-card-title class="text-h6">确认操作</v-card-title>
        <v-card-text>{{ actionConfirmMessage }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelAction" variant="outlined">取消</v-btn>
          <v-btn color="primary" @click="confirmAction" variant="outlined">确认</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    </template>

  </v-app>

</template>

<script setup>
import {ref, computed, onMounted, onBeforeUnmount, watch, nextTick, provide} from 'vue'
import trajectoryList from './trajectoryList.json'
import apiClient from './api/axios.js'
import envList from './envList.json'
import OperationRecord from './views/OperationRecord.vue'
import DualServoArm from './components/DualServoArm.vue'
import TrajectoryCard from './components/TrajectoryCard.vue'
import ServoCard from './components/ServoCard.vue'
import ServoDebugDialog from './components/ServoDebugDialog.vue'
import FunctionalTestDialog from './components/FunctionalTestDialog.vue'
import LoginPage from './components/LoginPage.vue'
import {
  AUTH_ENVIRONMENT_MISMATCH_EVENT,
  AUTH_EXPIRED_EVENT,
  buildAvatarPreviewUrl,
  clearAuthSession,
  fetchCurrentUser,
  loadAuthSession
} from './api/auth.js'
import { buildServoSendData } from './utils/sendData.js'
import { isNestedTrajectory } from './utils/trajectoryConfig.js'
import { useEmotionState } from './composables/useEmotionState.js'
import { useDevice } from './composables/useDevice.js'
import { useCombo } from './composables/useCombo.js'
import { useVelocityLimit } from './composables/useVelocityLimit.js'
import { useOperationRecord } from './composables/useOperationRecord.js'
import { useMirrorTrajectory } from './composables/useMirrorTrajectory.js'
import { useChart } from './composables/useChart.js'
import { useTrajectoryManager } from './composables/useTrajectoryManager.js'
import { useServoManager } from './composables/useServoManager.js'
import { useServoDebug } from './composables/useServoDebug.js'
import {
  emotionOptions,
  emotionStateOptions,
  backgroundActionStateOptions
} from './constants/emotions.js'

// ---------- 顶层通用状态 ----------
const authSession = ref(loadAuthSession())
const loginEnvironment = ref(resolveLoginEnvironment(authSession.value))
const currentUser = ref(null)
const userInfoLoading = ref(false)
const userInfoError = ref('')
const avatarLoadFailed = ref(false)
const logoutConfirmDialog = ref(false)
const totalDuration = ref(10)
const operationName = ref('test')
const emojiVersion = ref(1)
const selectedEmotion = ref(null)
const navigationExpanded = ref(false) // 导航栏展开状态

function resolveLoginEnvironment(session) {
  const managerUrl = session?.loginEnvironmentUrl || localStorage.getItem('selectedEnvManagerUrl')
  return envList.find(env => env.manager_url === managerUrl) || null
}

const loginEnvironmentName = computed(() => {
  return loginEnvironment.value?.name || authSession.value?.loginEnvironmentName || '—'
})

const userDisplayName = computed(() => {
  return currentUser.value?.nickName
    || currentUser.value?.nickname
    || currentUser.value?.username
    || '用户'
})

const isSuperAdmin = computed(() => Number(currentUser.value?.superAdmin) === 1)
const currentTenantId = computed(() => (
  currentUser.value?.tenantId
  || currentUser.value?.tenant?.id
  || authSession.value?.tenantId
  || authSession.value?.tenant?.id
  || null
))
const deviceTenantId = ref(
  Number(currentTenantId.value) > 0 ? currentTenantId.value : null
)

const userAvatarImageUrl = computed(() => {
  if (avatarLoadFailed.value) return ''
  return buildAvatarPreviewUrl(currentUser.value)
})

function handleAvatarError() {
  avatarLoadFailed.value = true
}

const userInfoItems = computed(() => {
  const user = currentUser.value || {}
  const merchantName = user.tenantName
    || user.tenant?.name
    || authSession.value?.tenantName
    || authSession.value?.tenant?.name
    || (user.superAdmin === 1 ? '平台管理员' : '')
    || '—'

  return [
    { label: '登录账号', value: user.username || '—' },
    { label: '手机号', value: user.phone || '—' },
    { label: '邮箱', value: user.email || '—' },
    { label: '商户名称', value: merchantName }
  ]
})

async function loadCurrentUserInfo() {
  if (!authSession.value) return

  userInfoLoading.value = true
  userInfoError.value = ''
  try {
    currentUser.value = await fetchCurrentUser(authSession.value)
    avatarLoadFailed.value = false
  } catch (error) {
    currentUser.value = null
    userInfoError.value = error.response?.data?.msg || error.message || '用户信息加载失败'
  } finally {
    userInfoLoading.value = false
  }
}

async function handleLoginSuccess(session) {
  const managerUrl = session?.loginEnvironmentUrl || localStorage.getItem('selectedEnvManagerUrl')
  const loginEnv = envList.find(env => env.manager_url === managerUrl)
  loginEnvironment.value = loginEnv || null
  if (loginEnv) {
    selectedEnv.value = loginEnv
  }
  authSession.value = session
  await loadCurrentUserInfo()
  fetchDeviceList()
}

function handleLogout() {
  logoutConfirmDialog.value = false
  clearAuthSession()
  resetAuthenticatedState()
}

function resetAuthenticatedState() {
  authSession.value = null
  loginEnvironment.value = null
  currentUser.value = null
  userInfoError.value = ''
  avatarLoadFailed.value = false
}

function handleLoginEnvironmentMismatch(event) {
  showSnackbarRetrigger(
    event.detail?.message || '当前登录环境不可访问其他服务数据'
  )
}

window.addEventListener(AUTH_EXPIRED_EVENT, resetAuthenticatedState)
window.addEventListener(AUTH_ENVIRONMENT_MISMATCH_EVENT, handleLoginEnvironmentMismatch)
const refreshTrigger = ref(0)
const isRestoring = ref(false) // 还原中标记（片段联动 watch 守卫，useOperationRecord 回写）
const operationRecordRef = ref(null)

// 轨迹列表数据
const trajectoryListData = ref(trajectoryList)

// 环境对象绑定
function resolveInitialEnv() {
  const savedManagerUrl = localStorage.getItem('selectedEnvManagerUrl')
  const savedEnv = envList.find(env => env.manager_url === savedManagerUrl)
  if (savedEnv) return savedEnv

  return envList[0] || {}
}

const selectedEnv = ref(resolveInitialEnv())
const baseManagerUrl = ref(selectedEnv.value.manager_url || '')

// 通用提示（供 composables 使用）
const angleSnackbar = ref(false)
const angleSnackbarMsg = ref('')
function showSnackbarMsg(msg) {
  angleSnackbarMsg.value = msg
  angleSnackbar.value = true
}
// 先关闭再打开，保证连续提示时动画重新触发（单舵机调试使用）
function showSnackbarRetrigger(msg) {
  angleSnackbar.value = false
  angleSnackbarMsg.value = msg
  setTimeout(() => {
    angleSnackbar.value = true
  }, 10)
}

// 设备管理（composables/useDevice.js）
const {
  deviceId,
  deviceList,
  deviceListLoading,
  currentServoList,
  servoVersion,
  fetchDeviceList,
  getSelectedDeviceServoVersion
} = useDevice({ selectedEnv, tenantId: deviceTenantId })

// 舵机最大速度限制（composables/useVelocityLimit.js）
const {
  velocityLimitDialog,
  servoVelocityMap,
  openVelocityLimitDialog,
  saveVelocityLimit
} = useVelocityLimit({ deviceId, selectedEnv, currentServoList, showSnackbar: showSnackbarMsg })

// 组合动作执行状态（composables/useCombo.js）
const {
  comboRunning,
  comboTotal,
  comboCurrentIndex,
  comboCurrentName,
  comboStatusMsg,
  stopCombo,
  handleRunCombo
} = useCombo({ deviceId, selectedEnv, showSnackbar: showSnackbarMsg })

// 功能性测试
const functionalTestDialog = ref(false)
const batchTestDeviceIds = ref([])
const batchTestLoopCount = ref(1)

function openFunctionalTestDialog() {
  const availableIds = new Set(deviceList.value.map(device => device.id))
  batchTestDeviceIds.value = batchTestDeviceIds.value.filter(id => availableIds.has(id))
  functionalTestDialog.value = true
}

function handleDeviceTenantChange(tenantId) {
  deviceTenantId.value = tenantId || null
  deviceId.value = ''
  fetchDeviceList()
}

function openServoAvailabilityCheck() {
  if (!deviceId.value) {
    showSnackbarMsg('请先选择需要校验的设备')
    return
  }
  functionalTestDialog.value = false
  openServoDebugDialog()
}

async function openBatchCombo() {
  if (batchTestDeviceIds.value.length === 0) {
    showSnackbarMsg('请至少选择一台测试设备')
    return
  }
  const opened = await operationRecordRef.value?.openComboForBatch({
    deviceIds: [...batchTestDeviceIds.value],
    loopCount: batchTestLoopCount.value
  })
  if (opened) functionalTestDialog.value = false
}

function handleComboRequest(payload) {
  handleRunCombo(payload, payload?.executionOptions || {})
}

// 表情和背景动作开关状态（composables/useEmotionState.js）
const {
  selectedEmotionState,
  selectedBackgroundActionState,
  actionConfirmDialog,
  actionConfirmMessage,
  handleEmotionStateChange,
  handleBackgroundActionStateChange,
  cancelAction,
  handleConfirmDialogClose,
  confirmAction
} = useEmotionState({ deviceId, selectedEnv, showSnackbar: showSnackbarMsg })

// 轨迹管理（composables/useTrajectoryManager.js）
const trajectoryManager = useTrajectoryManager({
  trajectoryListData,
  deviceId,
  getSelectedDeviceServoVersion,
  isRestoring,
  showSnackbar: showSnackbarMsg
})
const {
  trajectoryConfigs,
  currentTrajectoryId,
  currentTrajectory,
  dualServos,
  dualArmConfig,
  trajectoryX,
  trajectoryY,
  selectedTrajectoryConfig,
  showDualServoArm,
  showDualArmVisualization,
  selectTrajectoryDialog,
  selectPersonDialog,
  selectedTrajectoryName,
  deleteTrajectoryDialog,
  deleteTrajectoryName,
  openAddTrajectoryDialog,
  getTrajectoryPersons,
  selectTrajectoryName,
  selectTrajectory,
  confirmDeleteTrajectory,
  handleTrajectorySaved,
  exitDualServoArm,
  handleDualServosChanged,
  handleDualArmConfigChanged,
  handleEndPositionChanged,
  setCurrentAngleForDualServo,
  getAngleForServoIndex,
  addServoMotionForTrajectoryPoint
} = trajectoryManager

// 单舵机管理（composables/useServoManager.js）
const servoManager = useServoManager({
  dualServos,
  showDualServoArm,
  selectedTrajectoryConfig,
  selectedEnv,
  deviceId,
  totalDuration,
  isRestoring,
  showSnackbar: showSnackbarRetrigger
})
const {
  servos,
  sevroDebug,
  deleteServoDialog,
  fixServoParam,
  confirmDeleteServo,
  cancelDeleteServo
} = servoManager

// 舵机调试（composables/useServoDebug.js）
const {
  servoDebugDialog,
  servoDebugRunning,
  servoDebugForm,
  openServoDebugDialog,
  runAbsoluteAngleTest,
  stopAbsoluteAngleTest
} = useServoDebug({ selectedEnv, deviceId, showSnackbar: showSnackbarMsg })

// 图表渲染与拖拽（composables/useChart.js）
const {
  chartRef,
  showTrajectoryChart,
  renderChart,
  resizeChart,
  resolveNewServoName,
  startDrag
} = useChart({ servos, dualServos, trajectoryConfigs })

const showBottomPanel = computed(() => (
  showTrajectoryChart.value
  || (dualServos.value.length > 0 && showDualArmVisualization.value)
))

function toggleTrajectoryChart() {
  showTrajectoryChart.value = !showTrajectoryChart.value
  if (showTrajectoryChart.value) {
    nextTick(() => {
      renderChart()
      resizeChart()
    })
  }
}

function handleArmVisualizationToggle(showArm) {
  showDualArmVisualization.value = showArm
  if (!showArm) {
    showTrajectoryChart.value = true
    nextTick(() => {
      renderChart()
      resizeChart()
    })
  }
}

// 镜像轨迹与返程（composables/useMirrorTrajectory.js）
const {
  mirrorTrajectoryDialog,
  mirrorTrajectoryId,
  mirrorTargetTrajectoryName,
  addReturnTripDialog,
  getSourceTrajectoryForMirror,
  getMirrorPersonHint,
  openMirrorTrajectoryDialog,
  performMirrorTrajectory,
  openAddReturnTripDialog,
  confirmAddReturnTrip
} = useMirrorTrajectory({
  trajectoryConfigs, trajectoryListData, currentTrajectoryId, servoVersion,
  getSelectedDeviceServoVersion,
  getAngleForServoIndex,
  addServoMotionForTrajectoryPoint,
  showSnackbar: showSnackbarMsg
})

// 操作记录保存/还原（composables/useOperationRecord.js）
const {
  saveConfirmDialog,
  confirmSave,
  handleRestoreConfig
} = useOperationRecord({
  servos, trajectoryConfigs, currentTrajectoryId, showDualServoArm,
  trajectoryX, trajectoryY, trajectoryListData,
  selectedEnv, deviceId, servoVersion,
  operationName, totalDuration, selectedEmotion, emojiVersion, refreshTrigger,
  isRestoring,
  buildSendData: (saveToDevice, allowEmpty) => buildSendData(saveToDevice, allowEmpty),
  showSnackbar: showSnackbarMsg
})

// 提供给卡片子组件（TrajectoryCard.vue / ServoCard.vue）
provide('trajectoryManager', {
  ...trajectoryManager,
  openMirrorTrajectoryDialog,
  openAddReturnTripDialog
})
provide('servoManager', {
  ...servoManager,
  setCurrentAngleForDualServo
})

// 合并单舵机和双舵机的数据（传给 DualServoArm 显示）
const allServos = computed(() => {
  const allTrajectoryServos = trajectoryConfigs.value.flatMap(t => t.servos || [])
  return [...servos.value, ...dualServos.value, ...allTrajectoryServos]
})

// ---------- 添加舵机弹窗 ----------
const addDialog = ref(false)
const selectedServoIds = ref([]) // 选中的舵机ID数组

function openAddServoDialog() {
  selectedServoIds.value = []
  addDialog.value = true
}

function addServoFromList(servoConfig) {
  if (!servoConfig) return
  // 同名舵机自动追加序号（依据图表元数据判断是否已存在）
  const { servoName, servoNameIdx } = resolveNewServoName(servoConfig.name)
  servos.value.push({
    s: servoConfig.id,
    name: servoName,
    nameIdx: servoNameIdx,
    model: servoConfig.model,
    range: servoConfig.range,
    l: 1,
    angleDebug: 0,
    angleDebug2: 0,
    m: [{steps: 10, param: [0, 0, 0, 0]}]
  });
  addDialog.value = false
}

// 切换单个舵机选中状态
function toggleServoSelection(servoId) {
  const index = selectedServoIds.value.indexOf(servoId)
  if (index > -1) {
    selectedServoIds.value.splice(index, 1)
  } else {
    selectedServoIds.value.push(servoId)
  }
}

// 全选/取消全选
const isAllSelected = computed(() => {
  return currentServoList.value.length > 0 && selectedServoIds.value.length === currentServoList.value.length
})

function toggleSelectAll() {
  if (isAllSelected.value) {
    selectedServoIds.value = []
  } else {
    selectedServoIds.value = currentServoList.value.map(s => s.id)
  }
}

// 批量添加选中的舵机
function addSelectedServos() {
  if (selectedServoIds.value.length === 0) return
  const selectedServos = currentServoList.value.filter(s => selectedServoIds.value.includes(s.id))
  selectedServos.forEach(servo => addServoFromList(servo))
  selectedServoIds.value = []
  addDialog.value = false
}

// ---------- 导航 ----------
// 清空所有舵机和轨迹
function clearAllServosAndTrajectories() {
  servos.value = []
  trajectoryConfigs.value = []
  currentTrajectoryId.value = null
  navigationExpanded.value = false
  showSnackbarMsg('已清空所有舵机和轨迹')
}

// 滚动到指定元素
function scrollToElement(elementId) {
  nextTick(() => {
    const element = document.getElementById(elementId)
    if (element) {
      const container = document.querySelector('.top-panel .pa-3')
      if (container) {
        const elementTop = element.offsetTop - 20 // 添加一些偏移量
        container.scrollTo({
          top: elementTop,
          behavior: 'smooth'
        })
      } else {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  })
}

// ---------- 运行 / 下发 ----------
function sendMotion(name, obj, saveToDevice, emotionText) {
  const jsonString = JSON.stringify(obj)
  let p = {
    device_id: deviceId.value,
    params: {
      name: "self.servo.motion",
      arguments: {
        name: saveToDevice ? emotionText : 'default_run',
        operate: saveToDevice ? 2 : 1,
        version: emojiVersion.value !== null ? emojiVersion.value : 1,
        motion: jsonString
      }
    }
  }
  return p;
}

function buildSendData(saveToDevice, allowEmpty = false) {
  // 组装下发数据（纯计算逻辑在 utils/sendData.js）
  const sendData = buildServoSendData(servos.value, trajectoryConfigs.value)
  if (sendData.length === 0 && !allowEmpty) {
    showSnackbarMsg('请先添加舵机')
    return
  }
  if (saveToDevice) {
    return sendMotion(operationName.value, sendData, true, selectedEmotion.value || '')
  } else {
    return sendMotion(operationName.value, sendData, false)
  }
}

function runServos(saveToDevice) {
  // 允许无舵机配置时下发空 motion，设备端可按空动作正常处理。
  const p = buildSendData(saveToDevice, true)
  if (!p) return
  apiClient.post(selectedEnv.value.manager_url + '/xiaozhi/device/send-message', p, {
    headers: {'Content-Type': 'application/json'}
  }).then(res => {
    // 可选：成功提示
  }).catch(err => {
    // 可选：错误提示
  })
}

// ---------- watch / 生命周期 ----------
// 环境切换时自动刷新设备列表和同步 manager_url
watch(selectedEnv, (newEnv) => {
  deviceId.value = ''
  fetchDeviceList()
  if (newEnv && newEnv.manager_url) {
    localStorage.setItem('selectedEnvManagerUrl', newEnv.manager_url)
    baseManagerUrl.value = newEnv.manager_url
  }
  console.log('环境已切换:', selectedEnv.value);
  refreshTrigger.value++;
}, { immediate: true }) // 确保初始化时也触发

// 初始化时从 localStorage 读取
onMounted(() => {
  if (authSession.value) {
    loadCurrentUserInfo()
  }
  fetchDeviceList()
  fixServoParam(servos.value)
  renderChart()
})

onBeforeUnmount(() => {
  window.removeEventListener(AUTH_EXPIRED_EVENT, resetAuthenticatedState)
  window.removeEventListener(AUTH_ENVIRONMENT_MISMATCH_EVENT, handleLoginEnvironmentMismatch)
})

// 切换回单舵机视图时重新渲染图表
watch(showDualServoArm, (newVal) => {
  if (!newVal) {
    nextTick(() => {
      renderChart()
    })
  }
})

// 切换机械臂/角度图视图时重新调整图表大小
watch(showDualArmVisualization, () => {
  nextTick(() => {
    resizeChart()
  })
})
</script>

<style scoped>
html, body, #app, .v-application, .v-main {
  height: 100%;
  margin: 0;
  padding: 0;
  overflow: hidden;
  /* 隐藏最外层滚动条 */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
}

html::-webkit-scrollbar, body::-webkit-scrollbar, #app::-webkit-scrollbar, .v-application::-webkit-scrollbar, .v-main::-webkit-scrollbar {
  display: none;
}

.v-main {
  height: 100vh;
  overflow: hidden;
}

.split-container {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  position: relative;
  overflow: hidden;
}

.top-btn {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  margin-bottom: 0;
}

.page-header {
  display: flex;
  flex: 0 0 auto;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
}

.user-avatar-button {
  flex: 0 0 auto;
  margin: 12px 12px 0 0;
}

.user-info-menu-card {
  width: 280px;
  border-color: rgba(var(--v-border-color), var(--v-border-opacity));
}

.user-info-heading {
  display: flex;
  align-items: center;
  gap: 10px;
}

.user-info-identity {
  min-width: 0;
}

.user-info-name {
  overflow: hidden;
  font-size: 15px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-info-caption {
  margin-top: 2px;
  color: rgba(var(--v-theme-on-surface), 0.62);
  font-size: 11px;
}

.user-info-list {
  display: grid;
  gap: 6px;
  margin: 12px 0 0;
}

.user-info-item {
  display: grid;
  grid-template-columns: 68px minmax(0, 1fr);
  gap: 8px;
  font-size: 12px;
  line-height: 1.5;
}

.user-info-item dt {
  color: rgba(var(--v-theme-on-surface), 0.62);
}

.user-info-item dd {
  overflow: hidden;
  margin: 0;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.top-panel {
  flex-basis: 60%;
  min-height: 100px;
  max-height: 90%;
  overflow: hidden;
  transition: flex-basis 0.1s;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.bottom-panel-hidden .top-panel {
  flex-basis: 100% !important;
  max-height: 100%;
}

.top-panel > .v-container {
  height: 100%;
  overflow: auto;
}

.top-panel > .pa-3 {
  /* 细滚动条样式 */
  scrollbar-width: thin;
  scrollbar-color: #bbb #f5f5f5;
}

.top-panel > .pa-3::-webkit-scrollbar {
  width: 6px;
  background: #f5f5f5;
}

.top-panel > .pa-3::-webkit-scrollbar-thumb {
  background: #bbb;
  border-radius: 4px;
}

.splitter {
  height: 2px;
  background: #e0e0e0;
  cursor: row-resize;
  z-index: 10;
}

.bottom-panel {
  flex-basis: 40%;
  min-height: 100px;
  max-height: 90%;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
}

.chart-area {
  margin-bottom: 30px;
  width: 100%;
  height: 100%;
}

.chart-switch-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
}

/* Token验证相关样式 */
.page-content {
  height: 100vh;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.token-prompt {
  height: 100vh;
  background: #f5f5f5;
}

/* 环境按钮激活状态样式 */
.v-btn--active {
  background-color: rgba(var(--v-theme-primary), 0.1) !important;
  border-color: rgb(var(--v-theme-primary)) !important;
  color: rgb(var(--v-theme-primary)) !important;
}

/* 双舵机卡片样式 */
.dual-servo-card {
  border: 2px solid #1976d2 !important;
  background-color: rgba(25, 118, 210, 0.05) !important;
}

/* 当前选中的轨迹卡片样式 */
.trajectory-active {
  border: 3px solid #4caf50 !important;
  background-color: rgba(76, 175, 80, 0.1) !important;
}

</style>

<style>
/* 全局隐藏最外层滚动条（兼容Chrome/Safari/Edge/Firefox） */
html, body, #app, .v-application, .v-main {
  overflow: hidden !important;
  height: 100%;
}

/* 设置页面默认缩放比例为75%
html {
  zoom: 0.75;
} */

html::-webkit-scrollbar,
body::-webkit-scrollbar,
#app::-webkit-scrollbar,
.v-application::-webkit-scrollbar,
.v-main::-webkit-scrollbar {
  width: 0 !important;
  height: 0 !important;
  display: none !important;
}
</style>
